import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { useState, useEffect } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from './ui/carousel';
import { ImageWithFallback } from './figma/ImageWithFallback';
import {
  Smile,
  Clock,
  ThumbsUp,
  Download,
  Settings2,
  Smartphone,
  TrendingUp,
  SquareActivity,
  Zap,
  BadgeCheck,
} from 'lucide-react';
import first from '../assets/first.png';
import device from '../assets/device_phone_1_1.png';
import android from '../assets/android.png';
import ios from '../assets/ios.png';
import { motion } from 'framer-motion';

interface HomePageProps {
  t: any;
  language: string;
}

export default function HomePage({ t }: HomePageProps) {
  const [api, setApi] = useState<any>();
  // Авто-переключение каждые 10 секунд
  useEffect(() => {
    if (!api) return;

    const interval = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else {
        api.scrollTo(0); // Возврат к первому слайду
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [api]);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <ImageWithFallback
            src={first}
            alt="Happy child playing with gamepad"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black opacity-60"></div>
        </div>

        <div className="relative z-10 text-center max-w-4xl mx-auto px-4">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold mb-6 text-white"
            dangerouslySetInnerHTML={{ __html: t.heroTitle }}
          ></motion.h1>
          <Button
            size="lg"
            className="bg-emerald-800 hover:bg-emerald-900 text-white px-12 py-8 text-2xl fade-in-up"
            onClick={() => {
              const el = document.getElementById('device');
              if (el) {
                el.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            {t.heroButton}
          </Button>
        </div>
      </section>

      {/* Device Section */}
      <section id="device" className="py-20 bg-gradient-to-b from-[#1A1A1A] to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <ImageWithFallback
                src={device}
                alt="EnergyKID device"
                className="w-full h-auto rounded-lg shadow-2xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-emerald-600">{t.deviceTitle}</h2>
              <p className="text-lg text-gray-300 leading-relaxed flex flex-col">
                {t.deviceDescription}
                <div className="flex">
                  <img src={android} className="w-10 h-10 p-1 mt-3"></img>
                  <img src={ios} className="w-9 h-9 p-1 mt-3"></img>
                </div>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-emerald-600">
            {t.benefits.title}
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="bg-gray-800 border-gray-700 hover:border-emerald-500 transition-colors">
              <CardContent className="p-6 text-center">
                <Smile className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3 text-white">{t.benefits.benefit1.title}</h3>
                <p className="text-gray-300">{t.benefits.benefit1.description}</p>
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700 hover:border-emerald-500 transition-colors">
              <CardContent className="p-6 text-center">
                <Clock className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3 text-white">{t.benefits.benefit2.title}</h3>
                <p className="text-gray-300">{t.benefits.benefit2.description}</p>
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700 hover:border-emerald-500 transition-colors">
              <CardContent className="p-6 text-center">
                <ThumbsUp className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3 text-white">{t.benefits.benefit3.title}</h3>
                <p className="text-gray-300">{t.benefits.benefit3.description}</p>
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700 hover:border-emerald-500 transition-colors">
              <CardContent className="p-6 text-center">
                <Zap className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3 text-white">{t.benefits.benefit4.title}</h3>
                <p className="text-gray-300">{t.benefits.benefit4.description}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-emerald-600">
            {t.howItWorks.title}
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-emerald-800 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Download className="h-8 w-8 text-white" />
              </div>
              <div className="bg-emerald-800 w-1 h-8 mx-auto mb-4"></div>
              <h3 className="text-xl font-bold mb-3 text-white">{t.howItWorks.step1.title}</h3>
              <p className="text-gray-300">{t.howItWorks.step1.description}</p>
            </div>

            <div className="text-center">
              <div className="bg-emerald-800 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Settings2 className="h-8 w-8 text-white" />
              </div>
              <div className="bg-emerald-800 w-1 h-8 mx-auto mb-4"></div>
              <h3 className="text-xl font-bold mb-3 text-white">{t.howItWorks.step2.title}</h3>
              <p className="text-gray-300">{t.howItWorks.step2.description}</p>
            </div>

            <div className="text-center">
              <div className="bg-emerald-800 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Smartphone className="h-8 w-8 text-white" />
              </div>
              <div className="bg-emerald-800 w-1 h-8 mx-auto mb-4"></div>
              <h3 className="text-xl font-bold mb-3 text-white">{t.howItWorks.step3.title}</h3>
              <p className="text-gray-300">{t.howItWorks.step3.description}</p>
            </div>

            <div className="text-center">
              <div className="bg-emerald-800 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <div className="bg-emerald-800 w-1 h-8 mx-auto mb-4"></div>
              <h3 className="text-xl font-bold mb-3 text-white">{t.howItWorks.step4.title}</h3>
              <p className="text-gray-300">{t.howItWorks.step4.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Carousel Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Carousel
            className="w-full"
            setApi={setApi}
            opts={{
              loop: true,
            }}
          >
            <CarouselContent>
              <CarouselItem>
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-8 text-center">
                    <SquareActivity className="h-16 w-16 text-emerald-600 mx-auto mb-6" />
                    <h3 className="text-2xl font-bold mb-4 text-white">{t.carousel.item1.title}</h3>
                    <p
                      className="text-lg text-gray-300"
                      dangerouslySetInnerHTML={{ __html: t.carousel.item1.description }}
                    ></p>
                  </CardContent>
                </Card>
              </CarouselItem>

              <CarouselItem>
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-8 text-center">
                    <BadgeCheck className="h-16 w-16 text-emerald-600 mx-auto mb-6" />
                    <h3 className="text-2xl font-bold mb-4 text-white">{t.carousel.item2.title}</h3>
                    <p
                      className="text-lg text-gray-300"
                      dangerouslySetInnerHTML={{ __html: t.carousel.item2.description }}
                    ></p>
                  </CardContent>
                </Card>
              </CarouselItem>

              <CarouselItem>
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-8 text-center">
                    <BadgeCheck className="h-16 w-16 text-emerald-600 mx-auto mb-6" />
                    <h3 className="text-2xl font-bold mb-4 text-white">{t.carousel.item3.title}</h3>
                    <p
                      className="text-lg text-gray-300"
                      dangerouslySetInnerHTML={{ __html: t.carousel.item3.description }}
                    ></p>
                  </CardContent>
                </Card>
              </CarouselItem>
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex text-emerald-600 border-emerald-600 hover:bg-emerald-800 hover:text-white" />
            <CarouselNext className="hidden md:flex text-emerald-600 border-emerald-600 hover:bg-emerald-800 hover:text-white" />
          </Carousel>
        </div>
      </section>
    </div>
  );
}
